import {
  ApplyDialogData,
  CalendarDialogData,
  ChangeCalendarViewActionType,
  GetTasksForPlanningRequest,
  GetTasksForPlanningResponse,
  StartDraggingTaskActionType
} from './types';
import { actionCreator, asyncActionsCreator } from '../common/utils';
import {
  CalendarEvent,
  CalendarEventsResponse,
  CommitmentsDataResponse,
  CreateOrUpdateActivityRequest,
  CreateOrUpdateCourseRequest,
  DatesContainingTasksResponse,
  DeleteActivityResponse,
  DeleteCourseResponse,
  DeletePlannedTaskResponse,
  MarkAsUsedRequest,
  MovePlannedTaskRequest,
  PlannedTask,
  PlanTaskRequest,
  PlanTasksRequest,
  ReadingSourceInfoDto,
  ResizePlannedTaskRequest,
  TaskInfoDto,
  ThinActivityDto,
  ThinCourseDto,
  UpdatePlannedTaskRequest,
  UpdatePlannedTaskResponse
} from 'shovel-lib/types';
import { Moment } from 'moment';
import { PlanThisWeekRequest } from '../../api/shovelAiApi';
import { MarkUsedRequest, MarkUsedResponse, UnplanRequest } from '../../api/plannerApi';
import { UnplanScope } from '@api/plannerApi';
import { AutoPlanIntroSteps } from '@utils/calendar/autoPlanIntro';

const asyncActions = asyncActionsCreator('@@calendar');
const action = actionCreator('@@calendar');

export const calendarData = asyncActions<undefined, CalendarEventsResponse, string>('CALENDAR_DATA');

export const getDatesContainingTasks = asyncActions<
  { startDate: Moment | Date | string; endDate: Moment | Date | string },
  DatesContainingTasksResponse,
  string
>('DATES_CONTAINING_TASKS');

export const getPlannedTasks = asyncActions<undefined, CalendarEventsResponse, string>('PLANNED_TASKS');

export const commitmentsData = asyncActions<void, CommitmentsDataResponse, string>('COMMITMENTS_DATA');

export const changeCalendarView = action<ChangeCalendarViewActionType>('CHANGE_CALENDAR_VIEW');

/**
 * How a session's move or stretch is kept for Undo. `fromPopup`: made in a popup, where its date and then its time a
 * moment later are one step. `noUndo`: not a move at all (time logged on a used session stretches it).
 */
type SessionUndo = { fromPopup?: boolean; noUndo?: boolean };

// `logged`: a used session's block grown or shrunk by the time typed in its timer, not by a drag of its edge.
export const resizePlannedTask = asyncActions<
  ResizePlannedTaskRequest & { id: number; callback?: any; revert?: any; logged?: boolean } & SessionUndo,
  { oldEvent: CalendarEvent; newEvent: CalendarEvent },
  string
>('RESIZE_PLANNED_TASK');

// A session's new place shown before the server confirms it, so the block can float there at once (floatToNow.ts); put
// back with the old places if the save fails.
export const previewPlannedMoves = action<{ id: string | number; start: string; end: string }[]>('PREVIEW_PLANNED_MOVES');

/**
 * Bumpers (timer spec "Bumpers"): the dragged session's new place and the sessions it bumps (bump.ts planBumpers), saved
 * all at once, with Undo. `done` takes the calendar's own copy of the dropped block away once the store draws it.
 */
export const bumpSessions = action<{
  id: string;
  place: { start: number; end: number };
  moves: { id: string; start: number; end: number }[];
  pieces: { fromId: string; start: number; end: number }[];
  message: string;
  done?: () => void;
}>('BUMP_SESSIONS');

export const movePlannedTask = asyncActions<
  MovePlannedTaskRequest & { id: number; callback?: any } & SessionUndo,
  { oldEvent: CalendarEvent; newEvent: CalendarEvent },
  string
>('MOVE_PLANNED_TASK');

export const deletePlannedTask = asyncActions<number, { event: CalendarEvent; data: DeletePlannedTaskResponse }, string>(
  'DELETE_PLANNED_TASK'
);

export const getTasksOnDate = asyncActions<string, { tasks: TaskInfoDto[]; date: string }, string>('GET_TASKS_ON_DATE');

export const clearTasksOnDate = action<undefined>('CLEAR_TASKS_ON_DATE');

export const addCourse = action<undefined>('ADD_COURSE');

export const addActivity = action<undefined>('ADD_ACTIVITY');

export const changeEventDefaultColor = asyncActions<string, string, string>('CHANGE_EVENT_DEFAULT_COLOR');

export const createCourse = asyncActions<
  CreateOrUpdateCourseRequest & { defaultDurationInMinutes: number; isTaskList?: boolean },
  ThinCourseDto & { defaultDurationInMinutes: number },
  string
>('CREATE_COURSE');

export const updateCourse = asyncActions<
  ThinCourseDto & { defaultDurationInMinutes: number },
  ThinCourseDto & { defaultDurationInMinutes: number },
  string
>('UPDATE_COURSE');

export const deleteCourse = asyncActions<number, (DeleteCourseResponse & { refreshTaskList: boolean }) | {}, string>(
  'DELETE_COURSE'
);

export const createActivity = asyncActions<
  CreateOrUpdateActivityRequest & { isTaskList?: boolean },
  ThinActivityDto,
  string
>('CREATE_ACTIVITY');

export const updateActivity = asyncActions<ThinActivityDto, ThinActivityDto, string>('UPDATE_ACTIVITY');

export const deleteActivity = asyncActions<number, DeleteActivityResponse | {}, string>('DELETE_ACTIVITY');

// `bumps`: dropped on the calendar with Bumpers on (timer spec "Bumpers"), the new session bumps the ones it lands on.
// `bumped`: the sessions it bumped already drawn where they go, and where they were (Calendar.bumpSidebarDrop).
export const planTask = asyncActions<
  {
    request: PlanTaskRequest;
    infoRef?: any;
    bumps?: boolean;
    bumped?: { before: { id: number; start: string; end: string }[]; landsAt: number };
  },
  CalendarEvent,
  string
>('PLAN_TASK');

export const startDraggingTask = action<StartDraggingTaskActionType>('START_DRAGGING_TASK');

export const stopDraggingTask = action<undefined>('STOP_DRAGGING_TASK');

export const openCalendarDialog = action<CalendarDialogData>('OPEN_CALENDAR_DIALOG');

export const closeCalendarDialog = action<undefined>('CLOSE_CALENDAR_DIALOG');

export const showHideCalendarDialog = action<boolean>('SHOW_HIDE_CALENDAR_DIALOG');

export const updateCalendarSlatHeight = action<number>('UPDATE_CALENDAR_SLAT_HEIGHT');

export const openPopup = action<Function>('OPEN_POPUP');

export const closePopup = action<undefined>('CLOSE_POPUP');

export const clearState = action<undefined>('CLEAR_STATE');

export const toggleSidebar = action<undefined>('TOGGLE_SIDEBAR');

export const reorderPlannedTasksAgenda = asyncActions<
  { ordinalNumber: number; id: number; eventId: number; currentTasksOrder: any },
  { eventId: number; tasks: PlannedTask[] },
  { eventId: number; tasks: PlannedTask[]; error: string }
>('REORDER_PLANNED_TASKS_AGENDA');

export const startAgendaListener = action<undefined>('START_AGENDA_LISTENER');

export const stopAgendaListener = action<undefined>('STOP_AGENDA_LISTENER');

export const openEventApplyDialog = action<ApplyDialogData>('OPEN_EVENT_APPLY_DIALOG');

export const closeEventApplyDialog = action<undefined>('CLOSE_EVENT_APPLY_DIALOG');

export const openPlanBlockDialog = action<{ event: CalendarEvent; callback?: any }>('OPEN_PLAN_BLOCK_DIALOG');

export const closePlanBlockDialog = action<undefined>('CLOSE_PLAN_BLOCK_DIALOG');

export const getTasksForPlanning = asyncActions<GetTasksForPlanningRequest, GetTasksForPlanningResponse, string>(
  'GET_TASKS_FOR_PLANNING'
);

export const planTasks = asyncActions<PlanTasksRequest, CalendarEvent[], string>('PLAN_TASKS');

// With `replan`, the saga first unplans the range's Shovel-planned blocks, then rebuilds the request from the
// freed calendar before planning. `onDone(true)` once there is nothing more to wait for, just before the new sessions
// go on the calendar, so the dialog can close first and they come in in view; `onDone(false)` when planning failed.
export const planThisWeek = asyncActions<
  PlanThisWeekRequest & {
    replan?: { from: Date; to: Date; scope: UnplanScope };
    onDone?: (planned: boolean) => void;
    /** With "Add review sessions" on: the dates to make the previewed reviews for before planning (`reviewDates`). */
    reviewDates?: { from: string; to: string };
  },
  CalendarEvent[],
  string
>('PLAN_THIS_WEEK');

export const unplanWeek = asyncActions<
  UnplanRequest,
  { events: CalendarEvent[]; tasks: DeletePlannedTaskResponse[] },
  string
>('UNPLAN_WEEK');

/**
 * Marks several past blocks used at once, from the Auto-plan dialog's missed-block list. One action for the whole
 * set, like unplanWeek: a markAsUsed per block re-renders the calendar and refetches the pile each time.
 *
 * `onDone` is how the dialog, whose task list is its own state, learns to refetch — the same shape updateTaskStatus's
 * `callback` uses. `undoable`: one session's "Log time" on the calendar, kept for Undo (the Auto-plan dialog has its own
 * way of putting blocks back, which its list keeps track of).
 */
export const markBlocksUsed = asyncActions<
  MarkUsedRequest & { onDone?: (result: MarkUsedResponse) => void; undoable?: boolean },
  MarkUsedResponse,
  string
>('MARK_BLOCKS_USED');

export const resetNavigation = action<undefined>('RESET_NAVIGATION');

export const reorderCourses = asyncActions<ThinCourseDto[], ThinCourseDto[], string>('REORDER_COURSES');

export const reorderActivities = asyncActions<ThinActivityDto[], ThinActivityDto[], string>('REORDER_ACTIVITIES');

export const togglePlanSidebar = action<undefined>('TOGGLE_PLAN_SIDEBAR');

export const navigateFromMobile = action<Date>('NAVIGATE_FROM_MOBILE');

export const revertLastAction = action<undefined>('REVERT_LAST_ACTION');

export const updatePlannedTask = asyncActions<
  { id: number; request: UpdatePlannedTaskRequest; mutate?: boolean },
  { data: UpdatePlannedTaskResponse; mutate?: boolean },
  string
>('UPDATE_PLANNED_TASK');

export const openPlannedTaskDialog = action<any | undefined>('OPEN_PLANNED_TASK_DAILOG');

export const markAsUsed = asyncActions<
  {
    plannedTaskId: number;
    data: MarkAsUsedRequest;
    reading?: ReadingSourceInfoDto;
    courseId?: number;
    /** The minutes the session had before this log changed them (laps ticked or a time typed), for Undo to put back. */
    usedBefore?: number;
    /** The session whose popup this came from, which asks the book's question if it is still waiting for it. */
    askIn?: number;
  },
  UpdatePlannedTaskResponse,
  string
>('MARK_AS_USED');

export const duplicatePlannedTask = asyncActions<number, CalendarEvent & { notes: string }, string>(
  'DUPLICATE_PLANNED_TASK'
);

export const addTaskCategory = action<number>('ADD_TASK_CATEGORY');

export const highlightAiPlannedTasks = action<number[]>('HIGHLIGHT_AI_PLANNED_TASKS');

/** Auto-plan made review tasks: the lists that show tasks reload to pick them up. */
export const reviewsCreated = action<number>('REVIEWS_CREATED');
export const clearAiPlannedTasksHighlight = action<number[]>('CLEAR_AI_PLANNED_TASKS_HIGHLIGHT');
export const setAutoPlanIntroSteps = action<AutoPlanIntroSteps | null>('SET_AUTO_PLAN_INTRO_STEPS');
